import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Button, Card, CardBody, CardHeader, Col, Container, Form, FormGroup, Input, Label, Row,
} from "reactstrap";
import { useSelector } from "react-redux";
import Breadcrumb from "../common/breadcrumb";
import Loader from "../../shared/Loader";
import { errorToast, successToast } from "../../shared/Toast";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Badge } from "../reporting/ReportingShared";
import ConfigAuditLog from "./ConfigAuditLog";
import {
    getMembershipGroup,
    createMembershipGroup,
    updateMembershipGroup,
    setMembershipGroupHandler,
    setMembershipOverrides,
} from "../../utils/membershipApi";
import { STATUS_LABELS, formatCap, formatDiscount, CONFIG_ENTITIES } from "./membershipConstants";

/**
 * Create or edit a company group.
 *
 * ONE component for both routes, like membership-type-form.js — `params.id` decides the
 * mode. The pair of near-duplicate add/edit files elsewhere in this repo have already
 * drifted, and a company has a shared budget whose fields must not diverge.
 *
 * The edit mode carries two things a create cannot: the member list, and the handler
 * appointment. Both are deliberately on this screen rather than on each member's profile,
 * because "who is in this company and who runs it" is a question about the company.
 */

const sectionStyle = {
    fontSize: 16, fontWeight: 600, color: "#2c3e50", paddingBottom: 8,
    borderBottom: `2px solid ${C.borderLight}`, marginBottom: 16,
};

const labelStyle = { fontWeight: 500, fontSize: 14 };
const hintStyle = { fontSize: 12, color: C.textMed, marginTop: 4, display: "block" };

const ghostButton = {
    padding: "6px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600,
    border: `1px solid ${C.border}`, cursor: "pointer", color: C.textMed, background: C.white,
};

const EMPTY = {
    name: "",
    arabicName: "",
    maxDailyDiscountValue: "",
    maxMonthlyDiscountValue: "",
    internalNote: "",
};

/** "" is how an emptied number field arrives, and it must reach the API as null, not 0. */
const toNullableNumber = (value) => {
    if (value === null || value === undefined || String(value).trim() === "") return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
};

const sar = (value) => `SAR ${Number(value || 0).toFixed(2)}`;

const memberName = (m) =>
    [m?.user?.firstName, m?.user?.lastName].filter(Boolean).join(" ") || "—";

const MembershipGroupForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = Boolean(id);

    const userType = localStorage.getItem("userType");
    const loggedInMerchantId = localStorage.getItem("MerchantId");
    const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});

    const merchantId = userType === "merchant"
        ? loggedInMerchantId
        : (reduxSelectedMerchant && reduxSelectedMerchant !== "all") ? reduxSelectedMerchant : "";

    const [form, setForm] = useState(EMPTY);
    const [group, setGroup] = useState(null);
    const [isLoading, setIsLoading] = useState(isEdit);
    const [isSaving, setIsSaving] = useState(false);
    const [busyMemberId, setBusyMemberId] = useState(null);
    /**
     * Bumped when something on THIS page writes a configuration audit row, so the history
     * below refetches. Appointing a handler is the only such change — a save navigates
     * away, so those are covered by the next mount.
     */
    const [historyToken, setHistoryToken] = useState(0);

    const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

    const loadGroup = useCallback(async () => {
        if (!isEdit) return;
        setIsLoading(true);
        const data = await getMembershipGroup(id);
        setIsLoading(false);
        if (!data) { navigate("/membership/companies"); return; }

        setGroup(data);
        setForm({
            ...EMPTY,
            name: data.name ?? "",
            arabicName: data.arabicName ?? "",
            // null means no limit, and it has to come back as "" so the field reads as
            // empty rather than as a zero the admin never typed.
            maxDailyDiscountValue: data.maxDailyDiscountValue ?? "",
            maxMonthlyDiscountValue: data.maxMonthlyDiscountValue ?? "",
            internalNote: data.internalNote ?? "",
        });
    }, [id, isEdit, navigate]);

    useEffect(() => { loadGroup(); }, [loadGroup]);

    const validate = () => {
        if (!merchantId) return "Select a merchant first.";
        if (!form.name.trim()) return "Company name is required.";

        const daily = toNullableNumber(form.maxDailyDiscountValue);
        const monthly = toNullableNumber(form.maxMonthlyDiscountValue);
        if (daily !== null && daily < 0) return "A budget cannot be negative.";
        if (monthly !== null && monthly < 0) return "A budget cannot be negative.";
        // Not an error the API enforces, but a monthly budget below the daily one makes the
        // daily one unreachable — worth catching before it confuses somebody.
        if (daily !== null && monthly !== null && daily > monthly) {
            return "The daily budget is larger than the monthly one, so it can never be reached.";
        }
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const problem = validate();
        if (problem) { errorToast(problem); return; }

        const payload = {
            name: form.name.trim(),
            arabicName: form.arabicName || null,
            maxDailyDiscountValue: toNullableNumber(form.maxDailyDiscountValue),
            maxMonthlyDiscountValue: toNullableNumber(form.maxMonthlyDiscountValue),
            internalNote: form.internalNote || null,
            ...(isEdit ? {} : { merchantId: Number(merchantId) }),
            // The revision this form loaded, so a save that lost a race is refused with a
            // 409 rather than reverting somebody else's budget change. The payload carries
            // every field, including ones nobody here opened.
            ...(isEdit && group?.revision != null ? { revision: group.revision } : {}),
        };

        setIsSaving(true);
        const saved = isEdit
            ? await updateMembershipGroup(id, payload)
            : await createMembershipGroup(payload);
        setIsSaving(false);

        if (saved?.conflict) {
            // Reload so the admin sees what the other person changed before re-applying
            // theirs. The toast has already said why.
            loadGroup();
            return;
        }

        if (saved) {
            successToast(isEdit ? "Company updated" : "Company created");
            navigate("/membership/companies");
        }
    };

    /** Appoint, or stand down, the account handler. */
    const appoint = async (membershipId) => {
        setBusyMemberId(membershipId ?? "clear");
        const saved = await setMembershipGroupHandler(id, membershipId);
        setBusyMemberId(null);
        if (saved) {
            successToast(membershipId ? "Handler appointed" : "Handler cleared");
            loadGroup();
            setHistoryToken((n) => n + 1);
        }
    };

    /** Take a member out of the company. They become an individual, not a non-member. */
    const removeMember = async (member) => {
        const isHandler = Number(group?.handlerMembershipId) === Number(member.id);
        const warning = isHandler
            ? `${memberName(member)} is the account handler. Removing them from the company also clears the handler, so nobody will see the team view until you appoint someone else. Continue?`
            : `Remove ${memberName(member)} from ${group?.name}? They keep their card and their tier — they simply stop spending from the company budget. Their past spend stays with the company.`;
        if (!window.confirm(warning)) return;

        setBusyMemberId(member.id);
        const saved = await setMembershipOverrides(member.id, {
            groupId: null,
            reason: `Removed from ${group?.name}`,
        });
        setBusyMemberId(null);
        if (saved) {
            successToast("Member removed from the company");
            loadGroup();
        }
    };

    if (isLoading) return <Loader />;

    const usage = group?.usage || {};
    const members = group?.members || [];

    return (
        <Fragment>
            <Breadcrumb title={isEdit ? "Edit Company" : "Create Company"} parent="Membership" />
            <Container fluid>
                <style>{reportingGlobalCSS}</style>
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardHeader>
                                <h5 style={{ margin: 0 }}>{isEdit ? form.name || "Company" : "New Company"}</h5>
                                <span style={hintStyle}>
                                    A company's budget is <strong>shared</strong> across its members and counted in
                                    the merchant's timezone, net of returns. It does not decide the discount —
                                    that comes from each member's tier, and one company can span several tiers.
                                </span>
                            </CardHeader>
                            <CardBody>
                                {!merchantId && (
                                    <div style={{ padding: 16, marginBottom: 20, borderRadius: 8, background: C.orangeLight, color: C.text }}>
                                        Select a merchant from the header before creating a company.
                                    </div>
                                )}

                                <Form onSubmit={handleSubmit}>
                                    <div className="mb-4">
                                        <h6 style={sectionStyle}>Company details</h6>
                                        <Row>
                                            <Col md="6">
                                                <FormGroup>
                                                    <Label className="mb-2" style={labelStyle}>
                                                        Name <span style={{ color: C.coral }}>*</span>
                                                    </Label>
                                                    <Input
                                                        value={form.name}
                                                        onChange={(e) => set({ name: e.target.value })}
                                                        placeholder="e.g. Acme Corp"
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col md="6">
                                                <FormGroup>
                                                    <Label className="mb-2" style={labelStyle}>Arabic name</Label>
                                                    <Input
                                                        value={form.arabicName || ""}
                                                        onChange={(e) => set({ arabicName: e.target.value })}
                                                        placeholder="شركة أكمي"
                                                        dir="rtl"
                                                    />
                                                    <span style={hintStyle}>Shown to the handler in the app for Arabic users.</span>
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                    </div>

                                    <div className="mb-4">
                                        <h6 style={sectionStyle}>Shared budget</h6>
                                        <p style={{ ...hintStyle, marginTop: 0, marginBottom: 12 }}>
                                            Leave a field blank for <strong>no limit</strong>. Blank is not zero — a
                                            budget of zero would mean this company can never receive a discount.
                                            Both budgets are counted in Riyadh time and are net of returns: a fully
                                            returned order releases the budget it used.
                                        </p>
                                        <Row>
                                            <Col md="4">
                                                <FormGroup>
                                                    <Label className="mb-2" style={labelStyle}>Daily budget (SAR)</Label>
                                                    <Input
                                                        type="number" step="0.01"
                                                        value={form.maxDailyDiscountValue}
                                                        onChange={(e) => set({ maxDailyDiscountValue: e.target.value })}
                                                        placeholder="No limit"
                                                    />
                                                    <span style={hintStyle}>Caps a single day's spike across the whole company.</span>
                                                </FormGroup>
                                            </Col>
                                            <Col md="4">
                                                <FormGroup>
                                                    <Label className="mb-2" style={labelStyle}>Monthly budget (SAR)</Label>
                                                    <Input
                                                        type="number" step="0.01"
                                                        value={form.maxMonthlyDiscountValue}
                                                        onChange={(e) => set({ maxMonthlyDiscountValue: e.target.value })}
                                                        placeholder="No limit"
                                                    />
                                                    <span style={hintStyle}>The calendar month, not a rolling 30 days.</span>
                                                </FormGroup>
                                            </Col>
                                            {isEdit && (
                                                <Col md="4">
                                                    <FormGroup>
                                                        <Label className="mb-2" style={labelStyle}>Used so far</Label>
                                                        <div style={{ marginTop: 6, fontSize: 13, color: C.text }}>
                                                            <div>Today: <strong>{sar(usage.usedDailyValue)}</strong>{" "}
                                                                <span style={{ color: C.textLight }}>
                                                                    ({formatCap(usage.remainingDailyValue, " SAR")} left)
                                                                </span>
                                                            </div>
                                                            <div>This month: <strong>{sar(usage.usedMonthlyValue)}</strong>{" "}
                                                                <span style={{ color: C.textLight }}>
                                                                    ({formatCap(usage.remainingMonthlyValue, " SAR")} left)
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <span style={hintStyle}>
                                                            Net of returns. "No limit" left means no budget is set.
                                                        </span>
                                                    </FormGroup>
                                                </Col>
                                            )}
                                        </Row>
                                    </div>

                                    <div className="mb-4">
                                        <h6 style={sectionStyle}>Internal note</h6>
                                        <FormGroup>
                                            <Input
                                                type="textarea" rows="3"
                                                value={form.internalNote || ""}
                                                onChange={(e) => set({ internalNote: e.target.value })}
                                                placeholder="Admin eyes only"
                                            />
                                            <span style={hintStyle}>
                                                Never shown to the handler or to any member — not in the app, not in
                                                the team view.
                                            </span>
                                        </FormGroup>
                                    </div>

                                    <div style={{ display: "flex", gap: 10 }}>
                                        <Button color="primary" type="submit" disabled={isSaving || !merchantId}>
                                            {isSaving ? "Saving..." : isEdit ? "Save changes" : "Create company"}
                                        </Button>
                                        <Button color="light" type="button" onClick={() => navigate("/membership/companies")}>
                                            Cancel
                                        </Button>
                                    </div>
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {isEdit && (
                    <Row>
                        <Col sm="12">
                            <RCard
                                title={
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", flexWrap: "wrap", gap: 10 }}>
                                        <span>👥 Members <span style={{ color: C.textLight, fontWeight: 500, marginLeft: 8 }}>· {members.length}</span></span>
                                        {group?.handlerMembershipId && (
                                            <button
                                                style={ghostButton}
                                                disabled={busyMemberId === "clear"}
                                                onClick={() => appoint(null)}
                                            >
                                                Stand down the handler
                                            </button>
                                        )}
                                    </div>
                                }
                            >
                                {members.length === 0 ? (
                                    <div style={{ textAlign: "center", padding: 32, color: C.textMed }}>
                                        Nobody is in this company yet. Add members from{" "}
                                        <strong>Membership → Members</strong> — open a member and set their company.
                                    </div>
                                ) : (
                                    <div style={{ overflowX: "auto" }}>
                                        <table style={tbl.table}>
                                            <thead>
                                                <tr>
                                                    <th style={tbl.th}>Member</th>
                                                    <th style={tbl.th}>Card</th>
                                                    <th style={tbl.th}>Tier</th>
                                                    <th style={tbl.th}>Status</th>
                                                    <th style={tbl.th}>Handler</th>
                                                    <th style={tbl.th}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {members.map((m) => {
                                                    const isHandler = Number(group.handlerMembershipId) === Number(m.id);
                                                    return (
                                                        <tr key={m.id} className="rpt-row">
                                                            <td style={{ ...tbl.td, fontWeight: 600, color: C.text }}>
                                                                {memberName(m)}
                                                                {m.employeeId && (
                                                                    <div style={{ fontSize: 11, color: C.textLight, fontWeight: 400 }}>#{m.employeeId}</div>
                                                                )}
                                                            </td>
                                                            <td style={{ ...tbl.td, fontFamily: "monospace", fontSize: 12 }}>{m.membershipRef}</td>
                                                            <td style={tbl.td}>
                                                                {m.membershipType?.name || "—"}
                                                                <div style={{ fontSize: 11, color: C.textLight }}>
                                                                    {formatDiscount(m.membershipType?.discountType, m.membershipType?.discountValue)}
                                                                </div>
                                                            </td>
                                                            <td style={tbl.td}>{STATUS_LABELS[m.status] || m.status}</td>
                                                            <td style={tbl.td}>
                                                                {isHandler
                                                                    ? <Badge color={C.green} bg={C.greenLight}>Account handler</Badge>
                                                                    : (
                                                                        <button
                                                                            style={ghostButton}
                                                                            disabled={busyMemberId === m.id}
                                                                            onClick={() => appoint(m.id)}
                                                                        >
                                                                            Make handler
                                                                        </button>
                                                                    )}
                                                            </td>
                                                            <td style={tbl.td}>
                                                                <button
                                                                    style={{ ...ghostButton, color: C.coral }}
                                                                    disabled={busyMemberId === m.id}
                                                                    onClick={() => removeMember(m)}
                                                                >
                                                                    Remove from company
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                <div style={{ marginTop: 14, fontSize: 12, color: C.textMed }}>
                                    The <strong>account handler</strong> sees this company's members and each one's
                                    spend in the pickup app — status and totals only, never individual orders.
                                    Only an admin can appoint one, and only somebody already in this company.
                                </div>
                            </RCard>
                        </Col>
                    </Row>
                )}

                {/*
                  The company's own history: budget changes and handler appointments. The
                  handler one matters most — that person can see what everybody in the
                  company spends, so who granted it and when is worth keeping.
                */}
                {isEdit && (
                    <Row>
                        <Col sm="12">
                            <ConfigAuditLog
                                // The key remounts it after a handler change, which
                                // refetches and returns it to page 1 — where the new entry
                                // is. A save navigates away, so nothing else needs this.
                                key={historyToken}
                                entityType={CONFIG_ENTITIES.membershipGroup}
                                entityId={id}
                                merchantId={group?.merchantId || merchantId}
                                // The handler entry stores a membership id. This page has
                                // the member list, so it can say who that was — "not set →
                                // 7" is the one row nobody could read otherwise.
                                resolve={(field, value) => {
                                    if (field !== "handlerMembershipId" || value == null) return undefined;
                                    const who = members.find((m) => Number(m.id) === Number(value));
                                    // A member since removed from the company is not in the
                                    // list; the id is still better than nothing.
                                    return who ? memberName(who) : `Member #${value}`;
                                }}
                            />
                        </Col>
                    </Row>
                )}
            </Container>
        </Fragment>
    );
};

export default MembershipGroupForm;
